import { useIsAuthenticated } from '@azure/msal-react';
import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { DispatchType } from '../../../state/configureStore';
import { Col, Container, Row } from 'reactstrap';
import { ApplicationState } from '../../../state/ApplicationState';
import { fetchClientsThunk } from '../../../state/clients/reducer';
import { initializeApplicationThunk } from '../../../state/common/reducer';
import { fetchLicenceThunk } from '../../../state/licences/reducer';
import { fetchModulesThunk } from '../../../state/modules/reducer';
import { getRolesThunk } from '../../../state/roles/reducer';
import { fetchUsersThunk } from '../../../state/users/reducer';
import { getAllInstallersThunk } from '../../../state/versions/reducer';
import { AppBar } from '../../components/AppBar';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { StatusMessage } from '../../components/StatusMessage';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router';
import { ErrorComponent } from '../../components/ErrorComponent/ErrorComponent';
import { fetchDistributorsThunk } from '../../../state/distributors/reducer';
import { fetchLicenseTermsThunk } from '../../../state/licenseTerms/reducer';

export const AuthenticatedTemplate: FC = () => {
    const isAuthenticated = useIsAuthenticated();
    const activeRole = useSelector((state: ApplicationState) => state.common.activeRole);
    const dispatch = useDispatch<DispatchType>();
    const appInitializationPending = useSelector((state: ApplicationState) => state.common.appInitializationPending);
    const appInitializationError = useSelector((state: ApplicationState) => state.common.appInitializationError);

    useEffect(() => {
        if (isAuthenticated) {
            dispatch(initializeApplicationThunk());
            dispatch(fetchLicenceThunk());
            dispatch(fetchModulesThunk());
            dispatch(getAllInstallersThunk());
            dispatch(fetchClientsThunk());
            dispatch(fetchDistributorsThunk());
            if (activeRole === 2) {
                dispatch(fetchUsersThunk());
                dispatch(getRolesThunk());
                dispatch(fetchLicenseTermsThunk());
            }
        }
    }, [isAuthenticated, activeRole]);

    if (!isAuthenticated) {
        return <StatusMessage statusMessage={<FormattedMessage id="commonSessionHasExpired" />} />;
    }

    if (appInitializationError || appInitializationPending) {
        return <LoadingOverlay pending={appInitializationPending} errorMessage={appInitializationError} />;
    }

    return (
        <>
            <Container fluid id="root" className="g-0">
                <AppBar />
                <Row className="rootBody">
                    <Col className={'rootContent'}>
                        {!appInitializationPending && (
                            <ErrorBoundary FallbackComponent={ErrorComponent}>
                                <Outlet />
                            </ErrorBoundary>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
};
